/**
 * Widget API Hook
 *
 * Provides access to the authenticated API client within the widget context.
 * Updated to use unified apiService.
 */

import { useContext, useMemo } from 'react';
import { WidgetContext } from '../WidgetProvider';
import { createApiService } from '../../services/apiService';
import type { ApiService } from '../../services/apiService';

/**
 * Hook to access the widget API service
 * @returns Authenticated API service instance
 */
export function useWidgetAPI(): ApiService {
  const context = useContext(WidgetContext);

  if (!context) {
    throw new Error(
      'useWidgetAPI must be used within a WidgetProvider.'
    );
  }

  return useMemo(
    () => createApiService({
      baseUrl: context.config.apiBaseUrl,
      auth: context.config.auth,
      onError: context.config.onError,
    }),
    [context.config]
  );
}

/**
 * Hook to optionally access the widget API service
 * Returns null if used outside of WidgetProvider (useful for shared components)
 */
export function useOptionalWidgetAPI(): ApiService | null {
  const context = useContext(WidgetContext);

  return useMemo(() => {
    if (!context) {
      return null;
    }
    return createApiService({
      baseUrl: context.config.apiBaseUrl,
      auth: context.config.auth,
      onError: context.config.onError,
    });
  }, [context]);
}
